// Skills processing utilities for VA Sprouts
// Handles various skill formats: legacy strings, up-skills format, and database skills
import supabase from './supabase';

/**
 * Sanitizes skill names by removing special characters and normalizing the text
 * @param {string} skillName - The skill name to sanitize
 * @returns {string} Sanitized skill name
 */
export const sanitizeSkillName = (skillName) => {
  if (!skillName || typeof skillName !== 'string') return '';
  
  return skillName
    .trim()
    // Remove special characters but keep spaces, hyphens, and forward slashes
    .replace(/[^\w\s\-\/]/g, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim again after cleanup
    .trim();
};

/**
 * Safely extracts skill name from various skill formats
 * This prevents raw JSON data from being displayed and removes special characters
 * @param {string|object|any} skill - The skill in various formats
 * @returns {string} Clean skill name
 */
export const getSafeSkillName = (skill) => {
  // Handle null, undefined, or other falsy values
  if (!skill) return 'Unknown Skill';
  
  // Handle string skills (most common legacy format)
  if (typeof skill === 'string') {
    const sanitized = sanitizeSkillName(skill);
    return sanitized || 'Unknown Skill';
  }
  
  // Handle object skills (structured format)
  if (typeof skill === 'object' && skill !== null) {
    // Try various property names that might contain the skill name
    if (typeof skill.name === 'string' && skill.name.trim()) {
      return sanitizeSkillName(skill.name);
    }
    if (typeof skill.skill === 'string' && skill.skill.trim()) {
      return sanitizeSkillName(skill.skill);
    }
    if (typeof skill.title === 'string' && skill.title.trim()) {
      return sanitizeSkillName(skill.title);
    }
    if (typeof skill.skill_name === 'string' && skill.skill_name.trim()) {
      return sanitizeSkillName(skill.skill_name);
    }
    
    // If it's an object but no recognizable name field, log warning and return fallback
    console.warn('Skill object without recognizable name field:', skill);
    return 'Unknown Skill';
  }
  
  // Handle primitive values (numbers, booleans, etc.)
  if (typeof skill === 'number' || typeof skill === 'boolean') {
    return sanitizeSkillName(String(skill));
  }
  
  // Final fallback for any unexpected format
  console.warn('Unexpected skill format:', typeof skill, skill);
  return 'Unknown Skill';
};

/**
 * Parse skills array and return clean skill names
 * @param {Array|string|any} skills - Skills in various formats
 * @returns {Array<string>} Array of clean skill names
 */
export const parseSkillNames = (skills) => {
  if (!skills) return [];
  
  // Handle JSON string
  if (typeof skills === 'string') {
    try {
      const parsed = JSON.parse(skills);
      return Array.isArray(parsed) ? parsed.map(getSafeSkillName) : [getSafeSkillName(parsed)];
    } catch {
      // If not JSON, treat as comma-separated or single skill
      return skills.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
  }
  
  // Handle array
  if (Array.isArray(skills)) {
    return skills.map(getSafeSkillName);
  }
  
  // Handle single skill object
  if (typeof skills === 'object' && skills !== null) {
    return [getSafeSkillName(skills)];
  }
  
  return [];
};

/**
 * Extracts skill information from different skill formats
 * @param {string|object} skill - The skill in various formats
 * @returns {object} Normalized skill object
 */
export const normalizeSkill = (skill) => {
  // Handle string skills (legacy format)
  if (typeof skill === 'string') {
    return {
      name: skill.trim(),
      rating: 0,
      proficiency: 75, // Default proficiency for legacy skills
      experience: '',
      verified: false,
      upSkillsFormat: false,
      databaseSkill: false
    };
  }
  
  // Handle object skills (up-skills format or database format)
  if (typeof skill === 'object' && skill !== null) {
    const skillName = skill.name || skill.skill || '';
    const rating = skill.rating || 0;
    const isVerified = Boolean(skill.verified || skill.databaseSkill);
    const isUpSkillsFormat = Boolean(rating > 0 || skill.upSkillsFormat);
    const isDatabaseSkill = Boolean(skill.databaseSkill || skill.assessmentScore);
    
    // Calculate proficiency from various sources
    let proficiency = 75; // Default
    if (skill.proficiency && typeof skill.proficiency === 'number') {
      proficiency = skill.proficiency;
    } else if (rating > 0) {
      // Convert 1-5 rating to percentage (up-skills format)
      proficiency = rating * 20;
    } else if (skill.assessmentScore && skill.maxScore) {
      // Calculate from assessment score
      proficiency = Math.round((skill.assessmentScore / skill.maxScore) * 100);
    }
    
    return {
      name: skillName,
      rating: rating,
      proficiency: Math.min(100, Math.max(0, proficiency)),
      experience: skill.experience || '',
      verified: isVerified,
      upSkillsFormat: isUpSkillsFormat,
      databaseSkill: isDatabaseSkill,
      category: skill.category || 'Other',
      level: skill.level || 'Intermediate',
      assessmentScore: skill.assessmentScore || null,
      maxScore: skill.maxScore || null
    };
  }
  
  // Fallback for invalid formats
  return {
    name: 'Unknown Skill',
    rating: 0,
    proficiency: 50,
    experience: '',
    verified: false,
    upSkillsFormat: false,
    databaseSkill: false
  };
};

/**
 * Calculates a comprehensive skill match score between candidate skill and required skill
 * @param {object} candidateSkill - Normalized candidate skill object
 * @param {string} requiredSkillName - Name of the required skill
 * @param {number} skillWeight - Importance weight of this skill (from job frequency)
 * @returns {number} Match score for this skill
 */
export const calculateSkillMatchScore = (candidateSkill, requiredSkillName, skillWeight) => {
  // Check if the skill names match (case-insensitive)
  if (candidateSkill.name.toLowerCase() !== requiredSkillName.toLowerCase()) {
    return 0;
  }
  
  let skillScore = 0;
  
  // 1. Base skill match (40% of skill score)
  skillScore += skillWeight * 0.4;
  
  // 2. Proficiency bonus (30% of skill score)
  const proficiencyBonus = (candidateSkill.proficiency / 100) * skillWeight * 0.3;
  skillScore += proficiencyBonus;
  
  // 3. Rating bonus for up-skills format (15% of skill score)
  if (candidateSkill.rating >= 4) {
    // High ratings (4-5 stars) get full bonus
    skillScore += skillWeight * 0.15;
  } else if (candidateSkill.rating >= 3) {
    // Medium ratings (3 stars) get partial bonus
    skillScore += skillWeight * 0.1;
  } else if (candidateSkill.rating > 0) {
    // Low ratings (1-2 stars) get minimal bonus
    skillScore += skillWeight * 0.05;
  }
  
  // 4. Experience description bonus (10% of skill score)
  if (candidateSkill.experience && candidateSkill.experience.trim().length >= 50) {
    // Give more points for detailed experience descriptions
    if (candidateSkill.experience.length >= 180) {
      skillScore += skillWeight * 0.1; // Full bonus for detailed descriptions
    } else {
      skillScore += skillWeight * 0.05; // Partial bonus for shorter descriptions
    }
  }
  
  // 5. Verification bonus (5% of skill score)
  if (candidateSkill.verified || candidateSkill.databaseSkill) {
    skillScore += skillWeight * 0.05;
  }
  
  return skillScore;
};

/**
 * Calculates overall skills match percentage between candidate and job requirements
 * @param {Array} candidateSkills - Array of candidate skills in various formats
 * @param {Array} requiredSkills - Array of required skill names
 * @param {object} skillFrequency - Object mapping skill names to their importance weights
 * @returns {object} Match results with score and details
 */
export const calculateSkillsMatch = (candidateSkills, requiredSkills, skillFrequency = {}) => {
  if (!Array.isArray(candidateSkills) || !Array.isArray(requiredSkills)) {
    return { score: 0, matchedSkills: [], totalSkills: 0, matchedCount: 0 };
  }
  
  // Normalize all candidate skills
  const normalizedSkills = candidateSkills.map(normalizeSkill);
  
  let totalSkillScore = 0;
  let matchedSkills = [];
  let totalPossibleScore = 0;
  
  // Calculate total possible score from all required skills
  requiredSkills.forEach(requiredSkill => {
    const weight = skillFrequency[requiredSkill] || 1;
    totalPossibleScore += weight;
  });
  
  // Check each required skill against candidate skills
  requiredSkills.forEach(requiredSkill => {
    const weight = skillFrequency[requiredSkill] || 1;
    let bestMatch = null;
    let bestScore = 0;
    
    // Find the best matching skill from candidate
    normalizedSkills.forEach(candidateSkill => {
      const score = calculateSkillMatchScore(candidateSkill, requiredSkill, weight);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = candidateSkill;
      }
    });
    
    if (bestMatch) {
      totalSkillScore += bestScore;
      matchedSkills.push({
        requiredSkill,
        candidateSkill: bestMatch,
        score: bestScore,
        weight
      });
    }
  });
  
  // Calculate final percentage
  const matchPercentage = totalPossibleScore > 0 ? (totalSkillScore / totalPossibleScore) * 100 : 0;
  
  return {
    score: Math.min(100, Math.max(0, matchPercentage)),
    matchedSkills,
    totalSkills: requiredSkills.length,
    matchedCount: matchedSkills.length,
    rawScore: totalSkillScore,
    possibleScore: totalPossibleScore
  };
};

/**
 * Gets skill proficiency level label from numeric value
 * @param {number} proficiency - Proficiency percentage (0-100)
 * @returns {string} Human-readable proficiency level
 */
export const getProficiencyLabel = (proficiency) => {
  if (proficiency >= 90) return 'Expert';
  if (proficiency >= 80) return 'Advanced';
  if (proficiency >= 60) return 'Intermediate';
  if (proficiency >= 40) return 'Basic';
  return 'Beginner';
};

/**
 * Gets rating level label from star rating
 * @param {number} rating - Star rating (1-5)
 * @returns {string} Human-readable rating level
 */
export const getRatingLabel = (rating) => {
  if (rating >= 5) return 'Expert';
  if (rating >= 4) return 'Advanced';
  if (rating >= 3) return 'Intermediate';
  if (rating >= 2) return 'Basic';
  if (rating >= 1) return 'Beginner';
  return 'Not Rated';
};

/**
 * Checks if a skill has sufficient experience description for high ratings
 * @param {object} skill - Normalized skill object
 * @returns {boolean} Whether the skill meets experience requirements
 */
export const hasValidExperienceForRating = (skill) => {
  if (skill.rating <= 3) return true; // Lower ratings don't need experience
  if (skill.rating >= 4 && skill.experience && skill.experience.trim().length >= 180) {
    return true; // High ratings with sufficient experience
  }
  return false; // High ratings without sufficient experience
};

/**
 * Extracts skill names from various skill formats
 * @param {Array} skills - Array of skills in various formats
 * @returns {Array} Array of skill names as strings
 */
export const extractSkillNames = (skills) => {
  if (!Array.isArray(skills)) return [];
  
  return skills.map(skill => {
    if (typeof skill === 'string') return skill.trim();
    if (typeof skill === 'object' && skill !== null) {
      return skill.name || skill.skill || '';
    }
    return '';
  }).filter(name => name.length > 0);
};

/**
 * Predefined skill categories for use across the application
 * All skills are sanitized to remove special characters
 */
export const SKILL_CATEGORIES = {
  Administrative: [
    'Administrative Support', 'Calendar Management', 'Data Entry', 'Email Management', 
    'Research', 'Document Preparation', 'File Organization'
  ],
  Communication: [
    'Customer Service', 'Phone Handling', 'Email Communication', 'Chat Support',
    'Client Relations', 'Conflict Resolution'
  ],
  Technical: [
    'Web Development', 'WordPress', 'Shopify', 'HTML CSS', 'CRM Management',
    'Database Management', 'Technical Support'
  ],
  Marketing: [
    'Social Media Management', 'Content Writing', 'Digital Marketing', 'Email Marketing',
    'SEO', 'PPC', 'Lead Generation', 'Market Research'
  ],
  Creative: [
    'Graphic Design', 'Video Editing', 'Photo Editing', 'Illustration',
    'Animation', 'UI UX Design'
  ],
  Finance: [
    'Bookkeeping', 'Invoicing', 'Expense Tracking', 'Financial Reporting',
    'Budget Management', 'QuickBooks', 'Payroll Processing'
  ],
  Language: [
    'Translation', 'Multilingual Support', 'Proofreading', 'Editing',
    'Transcription', 'Content Localization'
  ],
  ProjectManagement: [
    'Project Management', 'Team Coordination', 'Workflow Optimization',
    'Task Management', 'Process Documentation', 'Quality Assurance'
  ]
};

/**
 * Gets all skills from all categories and custom skills from user profiles
 * @returns {Array} Array of all skill names as strings
 */
export const getAllSkills = () => {
  const allSkills = [];
  Object.values(SKILL_CATEGORIES).forEach(skillArray => {
    allSkills.push(...skillArray);
  });
  return [...new Set(allSkills)]; // Remove duplicates
};

/**
 * Process skills by handling comma-separated values, removing duplicates, sanitizing, and filtering out empty skills
 * @param {Array|string} inputSkills - Skills to process (can be array or comma-separated string)
 * @returns {Array} Cleaned, sorted array of unique skills
 */
export const processSkills = (inputSkills) => {
  // Handle empty input
  if (!inputSkills) return [];
  
  let skillsArray = [];
  
  // Convert to array if string
  if (typeof inputSkills === 'string') {
    // Split by commas and process each skill
    skillsArray = inputSkills.split(',');
  } else if (Array.isArray(inputSkills)) {
    // Process each item in the array
    skillsArray = inputSkills.flatMap(skill => {
      // If the skill is a string that might contain commas
      if (typeof skill === 'string' && skill.includes(',')) {
        return skill.split(',');
      }
      // If it's an object with a name property
      else if (typeof skill === 'object' && skill !== null) {
        const skillName = skill.name || skill.skill || '';
        // If the skill name contains commas, split it
        if (skillName && skillName.includes(',')) {
          return skillName.split(',');
        }
        return skillName || '';
      }
      // Regular string skill
      return skill;
    });
  } else {
    return [];
  }
  
  // Clean up each skill: sanitize, trim whitespace and filter out empty skills
  const cleanedSkills = skillsArray
    .map(skill => {
      const skillName = typeof skill === 'string' ? skill : getSafeSkillName(skill);
      return sanitizeSkillName(skillName);
    })
    .filter(skill => skill && skill.length > 0);
  
  // Remove duplicates and sort alphabetically
  return [...new Set(cleanedSkills)].sort();
};

/**
 * Async function to fetch all skills including custom ones from database
 * @returns {Promise<Array>} Promise resolving to array of all skill names
 */
export const fetchAllSkillsFromDatabase = async () => {
  try {
    // Start with predefined skills
    const predefinedSkills = getAllSkills();
    
    // Fetch custom skills from the database
    const { data: customSkills, error } = await supabase
      .from('user_profiles')
      .select('skills')
      .not('skills', 'is', null);
      
    if (error) throw error;
    
    // Extract skill names from the custom skills arrays
    const extractedCustomSkills = [];
    customSkills.forEach(profile => {
      if (Array.isArray(profile.skills)) {
        profile.skills.forEach(skill => {
          if (typeof skill === 'string') {
            // Handle potential comma-separated skills
            extractedCustomSkills.push(skill);
          } else if (skill && typeof skill === 'object' && skill.name) {
            // Handle object-style skills
            extractedCustomSkills.push(skill.name);
          }
        });
      }
    });
    
    // Combine all skills, clean them, and process with our helper
    return processSkills([...predefinedSkills, ...extractedCustomSkills]);
  } catch {
    return processSkills(getAllSkills()); // Fall back to predefined skills
  }
};

export default {
  sanitizeSkillName,
  normalizeSkill,
  calculateSkillMatchScore,
  calculateSkillsMatch,
  getProficiencyLabel,
  getRatingLabel,
  hasValidExperienceForRating,
  extractSkillNames,
  SKILL_CATEGORIES,
  getAllSkills,
  fetchAllSkillsFromDatabase
};
